import { Text } from '@netapp/bxp-design-system-react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { memo } from 'react';
import SuggestedPromptStyles from './SuggestedPrompt.module.scss';

/**
 * SuggestedPrompt - A single clickable suggested prompt chip
 * 
 * @param {Object} props
 * @param {string} props.id - Unique identifier for the component instance
 * @param {Object} props.prompt - Prompt object containing text
 * @param {function} props.onSuggestedPromptClick - Callback when prompt is clicked
 * @param {function} props.onAction - Callback for actions (alternative to onSuggestedPromptClick)
 * @param {string} props.className - Additional CSS class
 */

const SuggestedPrompt = memo(({ id, prompt, onSuggestedPromptClick, onAction, className }) => {
  const handleClick = () => {
    if (onSuggestedPromptClick) {
      onSuggestedPromptClick(prompt.text);
    } else if (onAction) {
      onAction('prompt_click', { text: prompt.text, promptId: prompt.id });
    }
  };

  return (
    <button
      className={classNames(SuggestedPromptStyles['suggested-prompt'], className)}
      onClick={handleClick}
      data-component-id={id}
      type="button"
    >
      <Text>{prompt.text}</Text>
    </button>
  );
});

SuggestedPrompt.displayName = 'SuggestedPrompt';

SuggestedPrompt.propTypes = {
  id: PropTypes.string,
  prompt: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    text: PropTypes.string.isRequired
  }).isRequired,
  onSuggestedPromptClick: PropTypes.func,
  onAction: PropTypes.func,
  className: PropTypes.string
};

SuggestedPrompt.defaultProps = {
  id: undefined,
  onSuggestedPromptClick: undefined,
  onAction: undefined,
  className: ''
};

// Catalog configuration for registry
SuggestedPrompt.catalogConfig = {
  type: 'SuggestedPrompt',
  category: 'input',
  defaultProps: {}
};

/**
 * SuggestedPromptGroup - A group of suggested prompt chips
 * 
 * @param {Object} props
 * @param {string} props.id - Unique identifier for the component instance
 * @param {Array} props.suggestedPrompts - Array of prompt objects
 * @param {Array} props.data - Array of prompt objects (from descriptor)
 * @param {function} props.onSuggestedPromptClick - Callback when any prompt is clicked
 * @param {function} props.onAction - Callback for actions
 * @param {string} props.className - Additional CSS class
 */

export const SuggestedPromptGroup = memo(({
  id,
  suggestedPrompts,
  data,
  onSuggestedPromptClick,
  onAction,
  className
}) => {
  // Support both 'suggestedPrompts' (direct) and 'data' (from descriptor)
  const prompts = suggestedPrompts || data || [];

  return (
    <div
      className={classNames(SuggestedPromptStyles['suggested-prompt-group'], className)}
      data-component-id={id}
    >
      {prompts.map((prompt, index) => (
        <SuggestedPrompt
          key={prompt.id || index}
          id={`${id}-prompt-${index}`}
          prompt={prompt}
          onSuggestedPromptClick={onSuggestedPromptClick}
          onAction={onAction}
        />
      ))}
    </div>
  );
});

SuggestedPromptGroup.displayName = 'SuggestedPromptGroup';

SuggestedPromptGroup.propTypes = {
  id: PropTypes.string,
  suggestedPrompts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      text: PropTypes.string.isRequired
    })
  ),
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      text: PropTypes.string.isRequired
    })
  ),
  onSuggestedPromptClick: PropTypes.func,
  onAction: PropTypes.func,
  className: PropTypes.string
};

SuggestedPromptGroup.defaultProps = {
  id: undefined,
  suggestedPrompts: [],
  data: [],
  onSuggestedPromptClick: undefined,
  onAction: undefined,
  className: ''
};

// Catalog configuration for registry
SuggestedPromptGroup.catalogConfig = {
  type: 'ChatSuggestions',
  category: 'input',
  defaultProps: {}
};

export default SuggestedPrompt;